import React, { useState } from "react";
import {
  useSlateStatic,
  useSelected,
  useFocused,
  ReactEditor
} from "slate-react";
import { Transforms } from "slate";
import { Popover, Space } from "antd";
import KoIcon from '@kwapio/icons';

const VideoElement = ({ attributes, element, children, url, readOnly }) => {
  const editor = useSlateStatic();
  const path = ReactEditor.findPath(editor, element);
  const selected = useSelected();
  const focused = useFocused();
  const [hovered, setHovered] = useState(false);
  const [width, setWidth] = useState(element.width || 500);
  const [height, setHeight] = useState(element.height || (width / 16) * 9);

  const deleteVideo = () => Transforms.removeNodes(editor, { at: path });
  const setAlignment = (align) =>
    Transforms.setNodes(editor, { align }, { at: path });

  const popoverContent = (
    <Space >
      <div
        style={{ padding: 4, cursor: "pointer" }}
        onMouseDown={(e) => { e.preventDefault(); setAlignment("left"); }}
      >
        <KoIcon name="LeftOutlined" color="#FFF" />
      </div>

      <div
        style={{ padding: 4, cursor: "pointer" }}
        onMouseDown={(e) => { e.preventDefault(); setAlignment("center"); }}
      >
        <KoIcon name="CenterOutlined" color="#FFF" />
      </div>

      <div
        style={{ padding: 4, cursor: "pointer" }}
        onMouseDown={(e) => { e.preventDefault(); setAlignment("right"); }}
      >
        <KoIcon name="RightOutlined" color="#FFF" />
      </div>

      <div
        style={{ padding: 4, cursor: "pointer", color: "#ff4d4f" }}
        onMouseDown={(e) => { e.preventDefault(); deleteVideo(); }}
      >
        <KoIcon name="DeleteOutlined" color="#FFF" width={14} height={14} />
      </div>

    </Space>
  );

  const handleResize = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    setHovered(true); // Keep border visible while dragging

    const startX = e.clientX;
    const startWidth = width;

    const editorContainer =
      e.currentTarget.closest(".slate-editor-wrapper") || document.body;
    const maxWidth = editorContainer.getBoundingClientRect().width - 32;

    const onMouseMove = (moveEvent) => {
      let deltaX = moveEvent.clientX - startX;
      if (direction.includes("left")) deltaX = -deltaX;

      let newWidth = Math.min(Math.max(200, startWidth + deltaX), maxWidth);
      let newHeight = (newWidth / 16) * 9;

      setWidth(newWidth);
      setHeight(newHeight);
      Transforms.setNodes(
        editor,
        { width: newWidth, height: newHeight },
        { at: path }
      );
    };

    const onMouseUp = () => {
      setHovered(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const renderCornerHandle = (pos) => (
    <div
      key={pos}
      style={{
        position: "absolute",
        [pos.includes("top") ? "top" : "bottom"]: "-6px",
        [pos.includes("left") ? "left" : "right"]: "-6px",
        width: "2rem",
        height: "2rem",
        backgroundColor: "#1677ff",
        borderRadius: "50%",
        cursor:
          pos === "top-right"
            ? `ne-resize`
            : pos === "bottom-left"
              ? `sw-resize`
              : pos === "bottom-right"
                ? `se-resize`
                : `nw-resize`,
        zIndex: 10
      }}
      onMouseDown={(e) => handleResize(e, pos)}
    />
  );

  const renderEdgeHandle = (side) => {
    const style = {
      position: "absolute",
      backgroundColor: "#1677ff",
      opacity: 0.5,
      zIndex: 10
    };

    if (side === "top" || side === "bottom") {
      style[side] = "-3px";
      style.left = 0;
      style.right = 0;
      style.height = "6px";
      style.cursor = `${side}-resize`;
    } else {
      style[side] = "-3px";
      style.top = 0;
      style.bottom = 0;
      style.width = "6px";
      style.cursor = `${side}-resize`;
    }

    return (
      <div key={side} style={style} onMouseDown={(e) => handleResize(e, side)} />
    );
  };

  const renderVideoBlock = () => (
    <div
      style={{
        display: "inline-block",
        position: "relative",
        width: `${width}px`,
        height: `${height}px`,
        border:
          hovered || (selected && focused)
            ? "2px solid #1677ff"
            : "2px solid transparent",
        borderRadius: "8px",
        overflow: "hidden"
      }}
    >
      <video
        src={url || element.url}
        width="100%"
        height="100%"
        controls
        style={{
          borderRadius: "8px",
          display: "block",
          objectFit: "cover"
        }}
      />

      {hovered && !readOnly && (
        <>
          {["right", "bottom"].map(renderEdgeHandle)}
          {["bottom-right"].map(renderCornerHandle)}
        </>
      )}
    </div>
  );

  return (
    <div
      {...attributes}
      contentEditable={false}
      className="slate-editor-wrapper"
      onMouseEnter={() => !readOnly && setHovered(true)}
      onMouseLeave={() => !readOnly && setHovered(false)}
      style={{
        textAlign: element.align || "center",
        margin: "1rem 0",
        position: "relative"
      }}
    >
      {!readOnly ? (
        <Popover
          content={popoverContent}
          placement="top"
          color="#222"
          getPopupContainer={(triggerNode) => triggerNode.parentElement}
        >
          {renderVideoBlock()}
        </Popover>
      ) : (
        renderVideoBlock()
      )}
      {children}
    </div>
  );
};

export default VideoElement;
